import Alert from "@atoms/Alert";
import ProgressBar from "@atoms/ProgressBar";
import Spinner from "@atoms/Spinner";
import { ProductRoutes, StorageKeys } from "@components/Data/enums";
import { getAge, getNYearsBack } from "@hooks/utils/date";
import { useLocalStorage } from "@hooks/utils/localStorage";
import {
  useGetUserIP,
  usePhoneGenerateOTP,
  usePhoneValidateOTP,
  usePostKyc,
} from "@hooks/api/kyc";
import TickMark from "@svgs/TickMark";
import LeftArrowIcon from "@svgs/LeftArrow";
import RightArrowIcon from "@svgs/RightArrow";
import { useRouter } from "next/router";
import { useState, ChangeEvent, useEffect, useMemo, FC, useRef } from "react";
import Ping from "components/atoms/CustomAnimations/Ping.js";
import PhoneInput from "@components/atoms/PhoneInput";
import { defaultCountry } from "../summary/data";
import en from "../../../public/locale/en";
import hi from "../../../public/locale/hi";
import ta from "../../../public/locale/ta";
import Image from "next/image";
import { RadioGroup } from "@headlessui/react";
import { RadioButton } from "../summary/DetailsForm";
import LeadFormDisclaimer from "@components/atoms/EthinosHomeLeft/LeadFormDisclaimer";
declare global {
  interface CredentialRequestOptions {
    otp?: { transport: string[] };
  }
}
declare global {
  interface LmSMTObj {
    page: (
      pageName: string,
      pageData: Record<string, any>,
      pageOptions: any[]
    ) => void;
    track: (pageName: string, pageData: Record<string, any>) => void;
    identify: (
      pageName: string,
      pageData: Record<string, any>,
      pageData2?: Record<string, any>
    ) => void;
  }
  interface Window {
    lmSMTObj: LmSMTObj;
    dataLayer: Record<string, any>[];
  }
}

const nameRegex = /^[a-zA-Z. ]+$/;
const phoneRegex = /([0-9])\1{6,}/;
const validPhoneRegex = (min, max) => new RegExp(`^\\d{${min},${max}}$`);

const constraints = {
  [ProductRoutes.REASSUREV2]: { max: 100, min: 18 },
  [ProductRoutes.SENIOR_FIRST]: { max: 76, min: 61 },
  [ProductRoutes.ASPIRE]: { max: 66, min: 18 },
  [ProductRoutes.HEALTH_RECHARGE]: { max: 66, min: 18 },
  [ProductRoutes.ASPIRE_SAVER]: { max: 66, min: 18 },
  default: { max: 101, min: 18 },
};

const surveyOptions = {
  primary: ["Yes", "No"],
  cover: [
    "Less than 3 lakhs",
    "Rs 3 Lakhs - Rs 5 lakh",
    "More than Rs 5 lakh",
    "Don't Remember",
  ],
};

const ViewPlanArticles: FC<{
  hideProgressBar?: boolean;
  multi?: boolean;
  productType?: string;
  isds: any;
  initData?: any;
  newDesignConfig?: boolean;
  showOTP?: boolean;
}> = ({
  hideProgressBar,
  multi,
  productType,
  isds,
  initData,
  newDesignConfig,
  showOTP = false,
}) => {
  const [activeStep, setActiveStep] = useState(1);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [retry, setRetry] = useState(false);
  // const [homeInfo, setHomeInfo] = useState("Health");

  const retailFlag = initData == undefined ? "true" : "false";

  const getNextButtonId = (idx: string) => {
    switch (idx) {
      case "0":
        return "start";
      case "1":
        return "phonenumber-next";
      case "2":
        return "name-next";
      case "3":
        return "dob-next";
      case "4":
        return "office-health-confirmation-next";
      default:
        return "start";
    }
  };

  const getBackButtonId = (idx) => {
    switch (idx) {
      case "2":
        return "name-back-button";
      case "3":
        return "dob-back-button";
      case "4":
        return "office-health-confirmation-back-button";
      default:
        return "back-button";
    }
  };

  const LuChevronRight = ({ id, size }) => {
    return (
      <svg
        stroke="currentColor"
        fill="none"
        stroke-width="2"
        viewBox="0 0 24 24"
        stroke-linecap="round"
        stroke-linejoin="round"
        id={id}
        width={size}
        height={size}
        xmlns="http://www.w3.org/2000/svg"
      >
        <path id={id} d="m9 18 6-6-6-6"></path>
      </svg>
    );
  };
  const LuChevronLeft = ({ id, size }) => {
    return (
      <svg
        stroke="currentColor"
        fill="none"
        stroke-width="2"
        viewBox="0 0 24 24"
        stroke-linecap="round"
        stroke-linejoin="round"
        id={id}
        width={size}
        height="34"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path id={id} d="m15 18-6-6 6-6"></path>
      </svg>
    );
  };

  function handleRedirect() {
    const homeInfoString = localStorage.getItem("homeInfo");
    if (homeInfoString) {
      try {
        const homeInfo = JSON.parse(homeInfoString);
        if (homeInfo?.insuranceType === "Travel") {
          router.push("/travel-insurance.html");
        } else if (homeInfo?.insuranceType === "Corporate") {
          router.push("/corporate-group-health-insurance.html");
        }
      } catch (error) {
        console.error("Error parsing homeInfo from localStorage:", error);
      }
    } else {
      console.error("homeInfo not found in localStorage.");
    }
  }

  const [getQuoteForm, setGetQuoteForm] = useLocalStorage(
    retailFlag == "true"
      ? StorageKeys.GET_QUOTE_FORM
      : StorageKeys.TELE_QUOTE_FORM,
    { ...initData, country: defaultCountry },
    retailFlag == "true"
  );
  const constraint =
    productType && Object.hasOwn(constraints, productType)
      ? constraints[productType]
      : constraints["default"];
  const age = useMemo(() => {
    const curAge = getQuoteForm?.dob ? getAge(getQuoteForm?.dob) : 0;
    return curAge <= 100 ? curAge : 0;
  }, [getQuoteForm?.dob]);
  const { data: ipAddress } = useGetUserIP();
  const [time, setTime] = useState(59);
  const interval = useRef<any>(null);
  const {
    trigger: generateOTP,
    isMutating: generatingOTP,
    data: generateOTPData,
    reset: resetGenerateOTPData,
  } = usePhoneGenerateOTP({
    onSuccess: (data) => {
      setOTP("");
      if (data?.success) {
        if (interval.current) {
          clearInterval(interval.current);
          setTime(59);
        }
        interval.current = setInterval(() => {
          setTime((prev) => {
            if (prev > 0) {
              return prev - 1;
            } else {
              interval.current && clearInterval(interval.current);
              return 60;
            }
          });
        }, 1000);

        if ("OTPCredential" in window) {
          const abortController = new AbortController();
          setTimeout(() => {
            abortController.abort();
          }, 2 * 60 * 1000);
          navigator.credentials
            .get({
              otp: { transport: ["sms"] },
              signal: abortController.signal,
            })
            .then((otpCredential) => {
              const otpCode = (otpCredential as any)?.code;
              if (otpCode) {
                setOTP(otpCode);
              }
            })
            .catch((err) => {
              console.error(err);
            });
        }
      }
    },
  });

  const {
    trigger: validateOTP,
    isMutating: validatingOTP,
    data: validateOTPData,
    reset: resetValidateOTPData,
  } = usePhoneValidateOTP({
    onSuccess: () => {
      setOTP("");
    },
  });
  const [otp, setOTP] = useState("");
  // const [surveyData, setSurveyData] = useState({ primary: null, cover: null });
  const { trigger, isMutating } = usePostKyc({
    onSuccess: (data) => {
      if (data.success) {
        if (activeStep === 3) {
          if (age <= 75) {
            // {business requirement hide}
            window.dataLayer &&
              window.dataLayer.push({
                event: "Health Insurance Form - Customer DOB",
                click_text: "DOB Filled",
                dob: getQuoteForm.dob,
                age: getAge(getQuoteForm?.dob),
                currentPage: window.location.href,
                nextPage: `${window.location.host}/health-insurance-plan/summary`,
              });
            window.dataLayer &&
              window.dataLayer.push({
                event: "Virtual Page Path",
                vpv: "vpv/health-insurance/customer-dob",
              });
            router.push({
              pathname: "/health-insurance-plan/summary",
              query: {
                multi: multi
                  ? multi
                  : initData && productType != "aspire-saver"
                  ? "true"
                  : "false",
                productType,
                retailFlag,
                isRelog: router.query.isRelog,
                copay: router.query.copay,
              },
            });
          } else {
            // {business requirement hide}
            window.dataLayer &&
              window.dataLayer.push({
                event: "Health Insurance Form - Customer DOB",
                click_text: "DOB Filled",
                dob: getQuoteForm.dob,
                age: getAge(getQuoteForm?.dob),
                currentPage: window.location.href,
                nextPage: `${window.location.host}/health-insurance-plan/summary`,
              });
            window.dataLayer &&
              window.dataLayer.push({
                event: "Virtual Page Path",
                vpv: "vpv/health-insurance/customer-dob",
              });
              router.push({
                pathname: "/health-insurance-plan/summary",
                query: {
                  multi: multi
                    ? multi
                    : initData && productType != "aspire-saver"
                    ? "true"
                    : "false",
                  productType,
                  retailFlag,
                  isRelog: router.query.isRelog,
                  copay: router.query.copay,
                },
            });
          }
        }
      }
    },
    onError: () => {
      setRetry(true);
    },
  });
  const router = useRouter();
  const { cid, utm_source, utm_campaign, utm_term, utm_medium,lang } = router.query;
  const payload = {
    ipAddress: ipAddress?.ip,
    campaignId: cid as string,
    utmLeadFields: {
      utmSource: utm_source as string,
      utmCampaign: utm_campaign as string,
      utmTerm: utm_term as string,
      utmMedium: utm_medium as string,
    },
    channel: getQuoteForm.channel || "Retail",
    subChannel: getQuoteForm.subChannel || "Website",
    department: getQuoteForm.department || "SWebsite",
    subDepartment: getQuoteForm.subDepartment || "Website",
    agentName: getQuoteForm.agentName || "",
    language : "english",
  };

  const onAlertClose = () => {
    setRetry(false);
  };

  const onMobileReset = () => {
    setGetQuoteForm((prev) => ({ ...prev, phoneNumber: "" }));
  };

  const onMobileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const phoneNumber = e.target.value;
    const maxLength = Number(getQuoteForm?.country?.maximum);
    const minLength = Number(getQuoteForm?.country?.minimum);
    if (validateOTPData) {
      resetValidateOTPData();
    }
    if (generateOTPData) {
      resetGenerateOTPData();
    }
    if (phoneNumber.length > maxLength) return;
    setGetQuoteForm((prev) => ({ ...prev, phoneNumber }));
    if (phoneNumber.length != maxLength && success) {
      setSuccess(false);
    }
    if (
      getQuoteForm?.country?.ISD == "+91" &&
      (phoneRegex.test(phoneNumber) ||
        ["1", "2", "3", "4"].includes(phoneNumber.charAt(0)))
    ) {
      setError(true);
      return;
    } else if (phoneNumber.length == maxLength) {
      setGetQuoteForm((prev) => ({ ...prev, phoneNumber }));

      if (validPhoneRegex(minLength, maxLength).test(phoneNumber)) {
        // {business requirement hide}
        window.dataLayer &&
          window.dataLayer.push({
            event: "Health Insurance Form - Customer Contact",
            click_text: "Contact Filled",
            currentPage: window.location.href,
            nextPage: window.location.href,
          });
        window.dataLayer &&
          window.dataLayer.push({
            event: "Virtual Page Path",
            vpv: "vpv/health-insurance/customer-contact",
          });
        window.lmSMTObj &&
          window.lmSMTObj.track(
            `${
              window.location.pathname == "/" ||
              window.location.pathname == "/v1" ||
              window.location.pathname == "/v0"
                ? "healthHomepageLead"
                : "healthGetQuoteLead"
            }`,
            {
              name: "health insurance",
              stepName: `${
                window.location.pathname == "/" ||
                window.location.pathname == "/v1" ||
                window.location.pathname == "/v0"
                  ? "Health Homepage Lead"
                  : "Health GetQuote Lead"
              }`,
              customUrl: window.location.href,
            }
          );
        window.lmSMTObj &&
          window.lmSMTObj.identify("", {
            phone: `+91${phoneNumber}`,
          });
        if (showOTP) {
          generateOTP({ phoneNumber });
        } else {
          onSuccesfullValidation(phoneNumber);
        }
      } else {
        setError(true);
        return;
      }
    }
    error && setError(false);
  };

  const onSuccesfullValidation = (currentPhoneNumber?: any) => {
    const forms = {
      country: {
        isd: getQuoteForm?.country?.ISD,
        name: getQuoteForm?.country?.country,
      },
    };
    if (getQuoteForm?.country?.ISD == "+91") {
      forms["phoneNumber"] = currentPhoneNumber || getQuoteForm?.phoneNumber;
    } else {
      forms["intPhoneNumber"] = currentPhoneNumber || getQuoteForm?.phoneNumber;
    }
    trigger(
      {
        data: {
          ...payload,
          forms,
        },
      },
      {
        onSuccess: (data) => {
          if (data?.success && data?.quoteId && data?.dialerId) {
            sessionStorage.setItem(StorageKeys.QUOTE_ID, data.quoteId);
            sessionStorage.setItem(StorageKeys.DIALER_ID, data.dialerId);

            const homeInfoString = localStorage.getItem("homeInfo");
            let insuranceType: string = "";
            if (homeInfoString !== null) {
              const homeInfo = JSON.parse(homeInfoString);
              insuranceType = homeInfo.insuranceType;
            } else {
            }
            handleRedirect();
            insuranceType === "Health" &&
              setActiveStep((activeStep) => activeStep + 1);
          }
        },
      }
    );
  };

  const handleOTP = (e) => {
    const val = e.target.value;
    if (isNaN(val) || val.length > 4) return;
    if (val.length == 4) {
      validateOTP(
        { otp: val, phoneNumber: getQuoteForm?.phoneNumber },
        {
          onSuccess: (data) => {
            if (!data?.success) return;
            onSuccesfullValidation();
          },
        }
      );
    }
    setOTP(val);
  };

  const isDisabled = () => {
    if (
      activeStep == 1 &&
      (!validPhoneRegex(
        Number(getQuoteForm?.country?.minimum),
        Number(getQuoteForm?.country?.maximum)
      ).test(getQuoteForm?.phoneNumber) ||
        (showOTP ? !validateOTPData?.success : false))
    ) {
      return true;
    }
    const name = getQuoteForm?.name;
    if (
      activeStep == 2 &&
      (!name || name?.length === 0 || !nameRegex.test(name))
    ) {
      return true;
    }
    if (activeStep === 3 && (age >= constraint.max || age < constraint.min)) {
      return true;
    }
    // if (
    //   activeStep === 4 &&
    //   (!surveyData?.primary ||
    //     (surveyData?.primary == "Yes" && !surveyData?.cover))
    // ) {
    //   return true;
    // }
    return isMutating || error;
  };

  const onViewPlan = () => {
    const formBody = {
      country: {
        isd: getQuoteForm?.country?.ISD,
        name: getQuoteForm?.country?.country,
      },
    };
    if (getQuoteForm?.country?.ISD == "+91") {
      formBody["phoneNumber"] = getQuoteForm?.phoneNumber;
    } else {
      formBody["intPhoneNumber"] = getQuoteForm?.phoneNumber;
    }
    if (activeStep == 1) {
      // {business requirement hide}
      window.dataLayer &&
        window.dataLayer.push({
          event: "Health Insurance Form - Customer Contact",
          click_text: "Contact Filled",
          currentPage: window.location.href,
          nextPage: window.location.href,
        });
      window.dataLayer &&
        window.dataLayer.push({
          event: "Virtual Page Path",
          vpv: "vpv/health-insurance/customer-contact",
        });
      window.lmSMTObj &&
        window.lmSMTObj.track(
          `${
            window.location.pathname == "/" ||
            window.location.pathname == "/v1" ||
            window.location.pathname == "/v0"
              ? "healthHomepageLead"
              : "healthGetQuoteLead"
          }`,
          {
            name: "health insurance",
            stepName: `${
              window.location.pathname == "/" ||
              window.location.pathname == "/v1" ||
              window.location.pathname == "/v0"
                ? "Health Homepage Lead"
                : "Health GetQuote Lead"
            }`,
            customUrl: window.location.href,
          }
        );
      window.lmSMTObj &&
        window.lmSMTObj.identify("", {
          phone: `+91${getQuoteForm.phoneNumber}`,
        });
      trigger(
        {
          data: {
            ...payload,
            forms: {
              ...formBody,
            },
          },
        },
        {
          onSuccess: (data) => {
            if (data?.success && data?.quoteId && data?.dialerId) {
              sessionStorage.setItem(StorageKeys.QUOTE_ID, data.quoteId);
              sessionStorage.setItem(StorageKeys.DIALER_ID, data.dialerId);

              const homeInfoString = localStorage.getItem("homeInfo");
              let insuranceType: string = "";
              if (homeInfoString !== null) {
                const homeInfo = JSON.parse(homeInfoString);
                insuranceType = homeInfo.insuranceType;
              } else {
              }
              handleRedirect();
              insuranceType === "Health" &&
                setActiveStep((activeStep) => activeStep + 1);
            }
          },
        }
      );
    }
    const quoteId = sessionStorage.getItem(StorageKeys.QUOTE_ID);
    const dialerId = sessionStorage.getItem(StorageKeys.DIALER_ID);
    formBody["name"] = getQuoteForm?.name?.toUpperCase() || "";
    formBody["dateOfBirth"] = getQuoteForm?.dob || "";

    if (activeStep == 2) {
      // {business requirement hide}
      window.dataLayer &&
        window.dataLayer.push({
          event: "Health Insurance Form - Customer Name",
          click_text: "Name Filled",
          currentPage: window.location.href,
          nextPage: window.location.href,
        });

      window.dataLayer &&
        window.dataLayer.push({
          event: "Virtual Page Path",
          vpv: "vpv/health-insurance/customer-name",
        });
      window.lmSMTObj &&
        window.lmSMTObj.track(
          `${
            window.location.pathname == "/" ||
            window.location.pathname == "/v1" ||
            window.location.pathname == "/v0"
              ? "healthUserName"
              : "healthUserName"
          }`,
          {
            name: "health insurance",
            stepName: `${
              window.location.pathname == "/" ||
              window.location.pathname == "/v1" ||
              window.location.pathname == "/v0"
                ? "Health User Name"
                : "Health User Name"
            }`,
            customUrl: window.location.href,
          }
        );

      window.lmSMTObj &&
        window.lmSMTObj.identify(
          "",
          {
            name: getQuoteForm.name,
          },
          {
            phone: `+91${getQuoteForm.phoneNumber}`,
          }
        );
      trigger(
        {
          data: {
            ...payload,
            forms: {
              ...formBody,
            },
          },
          quoteId,
          dialerId,
        },
        {
          onSuccess: (data) => {
            if (data?.success) {
              setGetQuoteForm((prev) => ({
                city: prev.city || data.prefillCity,
                ...prev,
              }));
              setActiveStep((activeStep) => activeStep + 1);
            }
          },
        }
      );
    }
    if (activeStep == 3) {
      window.lmSMTObj &&
        window.lmSMTObj.track("healthDobDetails", {
          name: "health insurance",
          stepName: "Form Submitted",
          dob: getQuoteForm.dob,
          customUrl: window.location.href,
        });

      trigger(
        {
          data: {
            ...payload,
            forms: {
              ...formBody,
            },
          },
          quoteId,
          dialerId,
        },
        {
          onSuccess: (data) => {
            if (data?.success) {
              const queryParams = {
                multi: "true",
                productType: "reassurev3",
                retailFlag,
                isRelog: router.query.isRelog,
                copay: router.query.copay,
              };
              router.push({
                pathname: "/health-insurance-plan/summary",
                query: queryParams,
              });
            }
          },
        }
      );
    }
  };

  const onBack = () => {
    if (activeStep == 2 && getQuoteForm?.phoneNumber.length) {
      setSuccess(true);
    }
    setActiveStep((activeStep) => activeStep - 1);
    error && setError(false);
  };

  const onNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setGetQuoteForm((prev) => ({ ...prev, name }));
    if (!nameRegex.test(name)) {
      setError(true);
      setSuccess(false);
      return;
    }
    name.length >= 1 ? setSuccess(true) : setSuccess(false);
    error && setError(false);
  };

  const onDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value;
    if (date && e.target.placeholder) e.target.placeholder = "";
    const age = getAge(date);
    if (age >= constraint.max || age < constraint.min) {
      setError(true);
    } else {
      error && setError(false);
    }
    setGetQuoteForm((prev) => ({ ...prev, dob: date }));
  };

  // the tab selected should be "Health" on page load
  useEffect(() => {
    localStorage.setItem(
      "homeInfo",
      JSON.stringify({ insuranceType: "Health" })
    );
  }, []);

  useEffect(() => {
    setRetry(false);
  }, [error, success]);
  const { locale } = useRouter();
  const t = locale === "en" ? en : locale === "hi" ? hi : ta;

  return (
    <div
      className={`${
        !hideProgressBar || !newDesignConfig ? "md:mt-8 mt-0" : ""
      } flex flex-col bg-white items-center justify-between w-full gap-6`}
    >
      {!hideProgressBar && (
        <ProgressBar
          activeStep={Math.min(activeStep, 3)}
          totalSteps={3}
          placeholder={["Mobile", "Name", "Date of birth"]}
        />
      )}
      <div
        className={`p-5 border-2 border-gray-300 rounded-md ${
          newDesignConfig
            ? "bg-white flex flex-row md:px-5 md:py-2 px-2 py-2 justify-between"
            : hideProgressBar
            ? "bg-gray-200 relative rounded-md mt-8 flex flex-col gap-3 shadow-xl"
            : "bg-[#F3F7F8] rounded-md mt-8 flex flex-col gap-3 shadow-xl"
        }   `}
      >
        {activeStep === 1 && (
          <>
            {!newDesignConfig && (
              <label
                className={`text-nivabupa-black md:text-lg font-medium ${
                  hideProgressBar && "relative"
                }`}
                htmlFor="mobile-number"
              >
                {t["Mobile Number"]}
              </label>
            )}
            {hideProgressBar && !newDesignConfig && (
              <Ping className="absolute -inset-x-[6px] -inset-y-2 md:h-5 md:w-5" />
            )}
            <div className="flex flex-col md:flex-row flex-wrap gap-4 md:gap-0">
              <PhoneInput
                success={success}
                isLoading={generatingOTP}
                isds={isds}
                inputValue={getQuoteForm?.phoneNumber}
                countryValue={getQuoteForm?.country || defaultCountry}
                onMobileChange={onMobileChange}
                onCountryChange={(val) => {
                  setSuccess(false);
                  setGetQuoteForm((prev) => ({
                    ...prev,
                    country: val,
                    phoneNumber: "",
                  }));
                }}
                isMask={initData?.channel == "Tele Caller" || false}
                onMobileReset={onMobileReset}
                newDesignConfig={newDesignConfig}
              />
              {showOTP && !generateOTPData && (
                <div className="p-4">
                  <button
                    className="whitespace-nowrap p-2 text-sm bg-nivabupa-blue-dark text-white w-max rounded-lg"
                    disabled={generatingOTP}
                    onClick={() => {
                      if (
                        validPhoneRegex(
                          Number(getQuoteForm?.country?.minimum),
                          Number(getQuoteForm?.country?.maximum)
                        ).test(getQuoteForm?.phoneNumber)
                      ) {
                        generateOTP({
                          phoneNumber: getQuoteForm?.phoneNumber,
                          resend: true,
                        });
                      } else setError(true);
                    }}
                  >
                    Send OTP
                  </button>
                </div>
              )}
            </div>
            {generateOTPData?.success && !validateOTPData?.success && (
              <div className="max-w-[356px] w-full flex flex-row bg-white text-sm p-2">
                <input
                  className="p-2 w-full placeholder:text-sm outline-none"
                  type="text"
                  placeholder="Enter OTP, sent on your mobile"
                  value={otp}
                  onChange={handleOTP}
                  autoComplete="one-time-code"
                />
                {validatingOTP && (
                  <div className="p-2">
                    <Spinner />
                  </div>
                )}
                {time != 60 ? (
                  <div className="p-2">{time}</div>
                ) : (
                  <div className="py-2">
                    <button
                      className="whitespace-nowrap p-2 bg-nivabupa-blue-dark text-white w-max rounded-lg"
                      disabled={generatingOTP}
                      onClick={() =>
                        generateOTP({
                          phoneNumber: getQuoteForm?.phoneNumber,
                          resend: true,
                        })
                      }
                    >
                      Resend OTP
                    </button>
                  </div>
                )}
              </div>
            )}
            {validateOTPData?.success == false && (
              <span className="text-red-700 text-xs">
                {validateOTPData?.msg}
              </span>
            )}
            {error && (
              <span className="text-red-700 text-xs">
                {t["Please enter valid mobile number"]}
              </span>
            )}
            {retry && (
              <Alert
                className="bg-red-100 max-w-[400px] text-red-700"
                content={
                  <button
                    className="text-sm
                  "
                    onClick={() => {
                      trigger({
                        ...payload,
                        forms: { phoneNumber: getQuoteForm?.phoneNumber },
                      });
                    }}
                  >
                    {t["Something went wrong."]}
                    <wbr />
                    {t["Click here to Retry."]}
                  </button>
                }
                onClose={onAlertClose}
              />
            )}
          </>
        )}
        {activeStep === 2 && (
          <>
            <label
              className="text-nivabupa-black md:text-lg font-medium my-auto text-base"
              htmlFor="name"
            >
              {/* {t["Name of Eldest Member to be insured"]} */}
              {t["Name"]}
            </label>
            <div className="flex flex-row items-center bg-white p-2 md:max-w-[350px] max-w-[250px]">
              <input
                id="name"
                autoFocus
                value={getQuoteForm?.name}
                className="w-full h-full uppercase placeholder:capitalize placeholder:text-nivabupa-black placeholder:opacity-50 outline-none placeholder:font-normal placeholder:text-sm"
                type="text"
                placeholder="Enter Your Name"
                required
                minLength={1}
                onChange={onNameChange}
              />
              {success && (
                <TickMark className="text-green-700 h-[24px] w-[24px]" />
              )}
            </div>
            {error && (
              <span className="text-red-700 text-xs">
                {t["Please enter valid name"]}
              </span>
            )}
            {retry && (
              <Alert
                className="bg-red-100 max-w-[400px] text-red-700"
                content={<span>Something went wrong.</span>}
                onClose={onAlertClose}
              />
            )}
          </>
        )}
        {activeStep === 3 && (
          <div className="flex items-center">
            <label
              className="text-nivabupa-black text-md font-medium"
              htmlFor="dob"
            >
              {/* {t["Date Of Birth of Eldest Member to be insured"]} */}
              Date of Birth
            </label>
            <div className="bg-white p-2 max-w-[276px] flex flex-row gap-4">
              <input
                id="dob"
                autoFocus
                className="placeholder:text-nivabupa-black placeholder:opacity-50 outline-none placeholder:font-normal placeholder:text-sm"
                type="date"
                placeholder={getQuoteForm?.dob ? "" : "dd/mm/yyyy"}
                required
                onChange={onDateChange}
                value={getQuoteForm?.dob}
                min={getNYearsBack(constraint.max)
                  .add(1, "day")
                  .format("YYYY-MM-DD")}
                max={getNYearsBack(constraint.min).format("YYYY-MM-DD")}
              />
              {age > 0 && (
                <span id="age" className="ml-auto text-black text-sm bg-nivabupa-background rounded-md py-1 px-2 text-center">
                  {age} Years
                </span>
              )}
            </div>
            {error && (
              <span className="text-red-700 text-xs">
                {`Please enter DOB between ${constraint.min}-${
                  constraint.max - 1
                } age.`}
              </span>
            )}
            {retry && (
              <Alert
                className="bg-red-100 max-w-[400px] text-red-700"
                content={<span>Something went wrong.</span>}
                onClose={onAlertClose}
              />
            )}
          </div>
        )}
        {/* {activeStep === 4 && (
          <div className="text-sm">
            <RadioGroup
              value={surveyData.primary}
              onChange={(val) =>
                setSurveyData((prev) => ({ ...prev, primary: val }))
              }
            >
              <RadioGroup.Label className={"flex flex-row items-center gap-3"}>
                <span className="text-lg">
                  Does your office provide health insurance for you ?
                </span>
              </RadioGroup.Label>
              <div className="mt-4 flex flex-row  gap-10 flex-wrap">
                {surveyOptions["primary"].map((value) => (
                  <RadioButton key={value} value={value} label={value} />
                ))}
              </div>
            </RadioGroup>
            {surveyData?.primary == "Yes" && (
              <RadioGroup
                className={"mt-8 text-sm"}
                value={surveyData.cover}
                onChange={(val) =>
                  setSurveyData((prev) => ({ ...prev, cover: val }))
                }
              >
                <RadioGroup.Label
                  className={"flex flex-row items-center gap-3"}
                >
                  <span className="text-lg">
                    What is the cover amount of your health insurance ?
                  </span>
                </RadioGroup.Label>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  {surveyOptions["cover"].map((value) => (
                    <RadioButton key={value} value={value} label={value} />
                  ))}
                </div>
              </RadioGroup>
            )}
          </div>
        )} */}
      </div>

      <div>
        <LeadFormDisclaimer />
      </div>

      <div
        className={`flex flex-row justify-between ${
          newDesignConfig ? "items-center gap-3" : ""
        }`}
        id={activeStep === 1 ? getBackButtonId(String(activeStep)) : ""}
      >
        {activeStep !== 1 && !newDesignConfig ? (
          <button
            className="flex flex-row items-center gap-1.5 mt-7 py-2 px-4 bg-nivabupa-blue-dark rounded-lg  text-white"
            onClick={onBack}
            id={getBackButtonId(String(activeStep))}
          >
            <LeftArrowIcon className="h-[24px] w-[24px]" />
            <span id={getBackButtonId(String(activeStep))} className="font-bold text-sm md:text-base">{t["BACK"]}</span>
          </button>
        ) : (
          activeStep !== 1 &&
          newDesignConfig && (
            <button
              className="h-full p-2 text-nivabupa-ethinosHomeOrange rounded-md border border-solid border-nivabupa-ethinosHomeOrange hover:text-white hover:bg-nivabupa-ethinosHomeOrange"
              onClick={onBack}
              id={getBackButtonId(String(activeStep))}
            >
              <LuChevronLeft size={23} id={getBackButtonId(String(activeStep))} />
            </button>
          )
        )}
        <button
          className={`flex flex-row items-center gap-1.5 md:px-4 px-4 rounded-xl text-white ml-auto disabled:opacity-50  ${
            newDesignConfig
              ? "md:h-[54px] h-[39px] 0 md:mr-7 mr-4 bg-nivabupa-ethinosHomeOrange "
              : "mt-7 py-2 bg-nivabupa-blue-dark"
          }`}
          disabled={isDisabled()}
          onClick={onViewPlan}
          id={getNextButtonId(String(activeStep))}
        >
          {isMutating ? (
            <Spinner />
          ) : (
            <>
              {!newDesignConfig ? (
                <span id={getNextButtonId(String(activeStep))} className="font-bold text-sm md:text-base uppercase">
                  {activeStep == 1 ? "start now" : "next"}
                </span>
              ) : (
                <div id={getNextButtonId(String(activeStep))} className="md:h-full md:w-full h-[10px] w-[8px] flex flex-row items-center">
                  <Image
                    src="/ethinos-home-page/svgs/right-chevron.svg"
                    width={10}
                    height={15}
                    alt="right chevron"
                    className="md:hidden flex"
                    id={getNextButtonId(String(activeStep))}
                  />
                  <span id={getNextButtonId(String(activeStep))} className="font-bold text-sm md:text-base uppercase md:flex hidden">
                    {activeStep == 1 ? "start now" : "next"}
                  </span>
                </div>
              )}
              {!newDesignConfig && (
                <LuChevronRight size={24} id={getNextButtonId(String(activeStep))} />
              )}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ViewPlanArticles;
